// Internal Modules
import { getLocale, logger } from '@/lib';
import type { DefaultInputState } from '@/store';
import { appStore } from '@/store';
import type { FormAddressState, FormState } from '@/store/models/form/types';
import { getAddressMetaKey } from '@/lib/utils/miscUtils';
import type { GenericObjectType } from '@/types';

import { getForcedValidCopy } from './formOperations';
import type { FormAddresses } from '@/store/models/form/types';
// Untyped Imports
// @ts-expect-error react-components
import { hasOwnProp, upperCase } from '@checkout/react-components';
import type { AddressType, RecursivePartial } from '@/types';
import type {
  EmptyObject,
  MappedAddressValues,
} from '@/lib/utils/shipping/types';
//@ts-expect-error untyped imports
import { isObject } from '@checkout/react-components';
/* === Begin Types === */
type getShippingAddressFromBillingAddressParams = {
  billingAddress: FormState['billingAddress'];
  firstName: DefaultInputState<string>;
  lastName: DefaultInputState<string>;
};

type getAddressMetaFromIdReturnType = {
  addressType: AddressType | null;
  isAddressField: boolean;
};

type AccountQuality = {
  accountQuality: {
    autoCompleteType?: string;
    isUserModified?: boolean;
  };
};
/* === End Types === */

/**
 * @function filterOutNonApplicableAddressFields
 * @param {object} address
 * @param {string} locale
 * @returns {object}
 * @description Takes the address object from the store and returns a new
 *  object with only the fields that apply to the country the address is for
 *  and only fields that are not empty or are empty but are required.
 */
const filterOutNonApplicableAddressFields = <
  T extends
    | FormAddressState['shippingAddress']
    | FormAddressState['billingAddress']
    | MappedAddressValues
    | EmptyObject,
>(
  address: T,
  addressMetaKey: string,
): RecursivePartial<T> => {
  const { api: apiState } = appStore.getState();
  const { address: addressMetadata } = apiState.griffinMetadata;
  const { layout = [] } = addressMetadata[addressMetaKey] || {};

  if (!layout.length) {
    logger.info('weasley_address_field_filter_missing_layout', {
      addressType: hasOwnProp(address, 'country') ? 'shipping' : 'billing',
      keyRequested: addressMetaKey,
      keysAvailable: Object.keys(addressMetadata),
    });
  }

  const result = layout.reduce<RecursivePartial<T>>(
    (accum, { name, isRequired }) => {
      const field = address[name as keyof T] || {};
      let fieldHasValue = false;

      // Handle different address types:
      // 1. Form address objects have {value: string, isValid: boolean, ...}
      // 2. MappedAddressValues have direct string values
      // 3. EmptyObject/missing fields are handled by the fallback to {}
      if (field && isObject(field) && 'value' in field) {
        // Form address field with .value property
        fieldHasValue = Boolean(
          typeof field.value === 'string' && field.value.length,
        );
      } else if (field && typeof field === 'string') {
        // Direct string value (MappedAddressValues)
        fieldHasValue = Boolean(field.length);
      }

      if (fieldHasValue || isRequired) {
        accum[name as keyof T] = address[name as keyof T];
      }
      return accum;
    },
    {},
  );

  return result;
};

/**
 * @function getShippingAddressFromBillingAddress
 * @param {object} paramsObj {billingAddress, firstName, lastName}
 * @returns {object}
 * @description creates a "valid" copy of the billing address with
 *  the additional fields firstName, lastName, and country. This is
 *  then used as the shipping address in certain situations, shipToBilling
 *  and when we don't allow shipping address to be changed but no shipping
 *  address was sent by the merchant. The result is "valid" in the sense
 *  that it won't go through any client side validations. If the billing
 *  address is invalid it will fail validations and the user will have to
 *  correct the errors and then resubmit the form. At that point we'll
 *  copy over the address again. We do this because since we aren't showing
 *  the shipping fields the user isn't able to correct any errors, so we don't
 *  want to tag any field as invalid.
 */
const getShippingAddressFromBillingAddress = ({
  billingAddress,
  firstName,
  lastName,
}: getShippingAddressFromBillingAddressParams): FormState['shippingAddress'] => ({
  accountQuality: billingAddress.accountQuality,
  country: {
    displayError: false,
    error: '',
    errorCode: '',
    isDirty: false,
    isValid: true,
    value: getLocale().country,
  },
  firstName: { ...firstName },
  lastName: { ...lastName },
  ...getForcedValidCopy(billingAddress),
});

// Returns address meta information from event id
const getAddressMetaFromId = (id: string): getAddressMetaFromIdReturnType => {
  const addressRegex = /^billing|^shipping|^residential/;
  const match = id.match(addressRegex);
  const addressType = match ? (match[0] as AddressType) : null;
  return {
    addressType,
    isAddressField: addressRegex.test(id),
  };
};

/**
 * @function getAccountQualityMetaDataFromAddress
 * @param {object} address
 * @returns {object}
 * @description Pull account quality values from addresses in form state and
 * construct the accountQuality object for form submission.
 */
const getAccountQualityMetaDataFromAddress = (
  address: FormAddresses,
): AccountQuality => {
  return {
    accountQuality: {
      autoCompleteType: address?.autoCompleteType?.value,
      isUserModified: address?.isManipulated?.value,
    },
  };
};

const mapAddressValueToName = <T extends FormAddresses>(
  address: T,
  addressKey: string,
): FormAddresses => {
  const {
    api: { griffinMetadata },
  } = appStore.getState();
  const { layout } = griffinMetadata.address[addressKey] || {};
  if (!layout) {
    logger.error('weasley_address_field_map_missing_layout', {
      keyRequested: addressKey,
      keysAvailable: Object.keys(griffinMetadata.address),
    });
    return address;
  }
  return layout.reduce((accum, { name }) => {
    if (hasOwnProp(address, name)) {
      // Pattern matching for the postal code field is not case sensitive
      // so we need to normalize it before we send it to the backend
      const value =
        name === 'postalCode'
          ? upperCase(address[name].value)
          : (address?.[name] as DefaultInputState).value;
      accum[name] = value.trim();
    }
    return accum;
  }, {} as T);
};

// TODO: improve typing on this function later
const serializeAddressFields = (
  data: FormState,
): Pick<
  FormState,
  'billingAddress' | 'residentialAddress' | 'shippingAddress'
> => {
  const addressFields: GenericObjectType = {};
  const { country } = getLocale();

  if (data.billingAddress) {
    addressFields.billingAddress = {
      ...mapAddressValueToName(data.billingAddress, getAddressMetaKey()),
      accountQuality: data.billingAddress.accountQuality,
      country,
      familyName: data.lastName.value?.trim(),
      givenName: data.firstName.value?.trim(),
    };
  }

  if (data.shippingAddress) {
    const { value: shipCountry } = data.shippingAddress.country;
    addressFields.shippingAddress = {
      ...mapAddressValueToName(
        data.shippingAddress,
        getAddressMetaKey(shipCountry),
      ),
      accountQuality: data.shippingAddress.accountQuality,
      country: shipCountry,
      familyName: data.shippingAddress.lastName.value?.trim(),
      givenName: data.shippingAddress.firstName.value?.trim(),
    };
  }

  if (data.residentialAddress) {
    addressFields.residentialAddress = {
      ...mapAddressValueToName(data.residentialAddress, getAddressMetaKey()),
      accountQuality: data.billingAddress.accountQuality,
      country,
      familyName: data.lastName.value?.trim(),
      givenName: data.firstName.value?.trim(),
    };
  }

  return addressFields as Pick<
    FormState,
    'billingAddress' | 'residentialAddress' | 'shippingAddress'
  >;
};

export {
  filterOutNonApplicableAddressFields,
  getAccountQualityMetaDataFromAddress,
  getAddressMetaFromId,
  getAddressMetaKey,
  getShippingAddressFromBillingAddress,
  mapAddressValueToName,
  serializeAddressFields,
};
